/**
 * Helpers for converting between the backend `localizationMetadata` shape and
 * the flat `localizations` array the localized-metadata directive binds to.
 *
 * Backend contract (per MW-12096 / MW-12612 / MW-12686 / MW-12688 / MW-12614
 * and the content-set / partner-product tasks): `localizationMetadata` is a
 * map keyed by ISO-639-1 two-letter code, e.g.
 *
 *   { "de": { "name": "Der Titel", "horizontalImage": "…", "verticalImage": "…" } }
 *
 *   null  -> field ignored (no change to stored localizations)
 *   {}    -> remove all stored localizations
 *
 * The directive works with an array of cards instead, each `{ language, … }`.
 * `pairs` maps backend wire keys to the UI/model keys used inside a card; image
 * key names differ per entity (e.g. VOD uses `imageUrl` / `imageBackgroundUrl`
 * while the backend uses `verticalImage` / `horizontalImage`), so the caller
 * supplies the mapping. Fields whose wire and UI names match are listed as
 * `[k, k]`.
 *
 * @module app/helpers/localization
 */

/**
 * Convert a backend `localizationMetadata` map into a `localizations` array.
 *
 * @param  {Object<string,Object>} map  - backend map keyed by language code
 * @param  {Array<[string,string]>} pairs - [wireKey, uiKey] mappings
 * @return {Object[]} array of `{ language, … }` localization cards
 */
export function metadataMapToLocalizations (map, pairs) {
  if (!map || typeof map !== 'object') {
    return []
  }
  return Object.keys(map).map((language) => {
    const wire = map[language] || {}
    const loc = { language }
    pairs.forEach(([wireKey, uiKey]) => {
      if (wire[wireKey] != null) {
        loc[uiKey] = wire[wireKey]
      }
    })
    return loc
  })
}

/**
 * Convert a `localizations` array back into a backend `localizationMetadata`
 * map. Returns `undefined` when there is no array to send (so the field is
 * omitted from the payload = "ignore"), or `{}` for an empty array (= "remove
 * all localizations"), matching the backend semantics above.
 *
 * @param  {Object[]} localizations    - array of `{ language, … }` cards
 * @param  {Array<[string,string]>} pairs - [wireKey, uiKey] mappings
 * @return {Object<string,Object>|undefined}
 */
export function localizationsToMetadataMap (localizations, pairs) {
  if (!Array.isArray(localizations)) {
    return undefined
  }
  return localizations.reduce((map, loc) => {
    if (!loc || !loc.language) {
      return map
    }
    const wire = {}
    pairs.forEach(([wireKey, uiKey]) => {
      const value = loc[uiKey]
      if (value != null && value !== '') {
        wire[wireKey] = value
      }
    })
    map[loc.language] = wire
    return map
  }, {})
}

/**
 * Field pairs for the "show family" localization DTO shared by VOD, TV Show,
 * Season, Episode and Live Event (backend `ShowLocalizationMetadataDto`).
 * The image field names differ per entity, so the model passes its own
 * vertical / horizontal image field names.
 *
 * @param  {string} verticalUiKey   - model field holding the vertical poster URL
 * @param  {string} horizontalUiKey - model field holding the horizontal poster URL
 * @return {Array<[string,string]>}
 */
export function showLocalizationPairs (verticalUiKey, horizontalUiKey) {
  return [
    ['name', 'name'],
    ['description', 'description'],
    ['directors', 'directors'],
    ['stars', 'stars'],
    ['verticalImage', verticalUiKey],
    ['horizontalImage', horizontalUiKey]
  ]
}

/**
 * Field pairs for the App-Customization localization DTOs (text only):
 *  - MODULE  (ModuleLocalizationMetadataDto): title only.
 *  - ROW     (RowLocalizationMetadataDto):    title + description.
 *  - SHELF   (ShelfLocalizationMetadataDto):  title + description, but the
 *            shelf directive stores the localized title under `name`
 *            (its nameKey), so the wire `title` maps to the UI `name`.
 */
export const MODULE_LOCALIZATION_PAIRS = [['title', 'title']]
export const ROW_LOCALIZATION_PAIRS = [['title', 'title'], ['description', 'description']]
export const SHELF_LOCALIZATION_PAIRS = [['title', 'name'], ['description', 'description']]

/**
 * Field pairs for Bundles / Sets / Partner Products. Wire and UI names are
 * identical here (no remap), so each pair is [k, k].
 *  - CONTENT_SET     (ContentSetLocalizationMetadataDto):     name, description, coverImageUrl.
 *  - PARTNER_PRODUCT (PartnerProductLocalizationMetadataDto): name, description, coverImageUrl, verticalImageUrl.
 */
export const CONTENT_SET_LOCALIZATION_PAIRS = [['name', 'name'], ['description', 'description'], ['coverImageUrl', 'coverImageUrl']]
export const PARTNER_PRODUCT_LOCALIZATION_PAIRS = [['name', 'name'], ['description', 'description'], ['coverImageUrl', 'coverImageUrl'], ['verticalImageUrl', 'verticalImageUrl']]
